import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Wrench, ChevronRight, Package, FileText } from "lucide-react";
import api from "../lib/api";
import { PageHeader, BrandMark } from "../components/Layout";
import { useLang } from "../context/LanguageContext";
import { StatusChip } from "./Reports";

export default function Machines() {
  const nav = useNavigate();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/machines").then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageHeader title={t("Machines")} subtitle={t("Machines used across your company's reports")} />
      <div className="px-4 pt-4">
        {loading && <p className="text-zinc-500 text-center py-10">{t("Loading...")}</p>}
        {!loading && items.length === 0 && <p className="text-zinc-500 text-center py-10" data-testid="machines-empty">{t("No machines yet.")}</p>}
        <div className="space-y-2.5 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-3 md:space-y-0">
          {items.map((m) => (
            <button key={m.serial_number} data-testid={`machine-row-${m.serial_number}`}
              onClick={() => nav(`/machines/${encodeURIComponent(m.serial_number)}`)}
              className="w-full text-left bg-[#18181B] border border-zinc-800 hover:border-[#F26722]/50 rounded-2xl p-4 transition-colors flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-bold text-white font-mono flex items-center gap-2"><Wrench size={16} className="text-[#F26722] shrink-0" /> {m.serial_number}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {m.report_count} {m.report_count === 1 ? t("report") : t("reports")}
                  {m.parts != null ? ` · ${m.parts} ${t("parts")}` : ""}
                  {m.last_date ? ` · ${t("last used")} ${m.last_date}` : ""}
                </p>
              </div>
              <ChevronRight size={18} className="text-zinc-600 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export function MachineHistory() {
  const { serial } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false); setData(null);
    api.get(`/machines/history`, { params: { serial } }).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [serial]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto md:max-w-2xl border-x border-zinc-800/40">
        <div className="px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
          <button onClick={() => nav("/machines")} className="flex items-center gap-2 text-zinc-300" data-testid="machine-back-btn"><ArrowLeft size={20} /> {t("Back")}</button>
          <BrandMark />
        </div>
        <div className="px-6 py-16 text-center" data-testid="machine-history-error">
          <Wrench size={40} className="text-zinc-700 mx-auto" />
          <p className="text-white font-bold mt-4 font-mono">{serial}</p>
          <p className="text-zinc-500 mt-2">{t("Machine history isn't available yet.")}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-zinc-500 flex items-center justify-center">{t("Loading...")}</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-zinc-300" data-testid="machine-back-btn"><ArrowLeft size={20} /> {t("Back")}</button>
        <BrandMark />
      </div>
      <div className="px-5">
        <h1 className="text-2xl font-extrabold text-white mt-5 font-mono flex items-center gap-2"><Wrench size={20} className="text-[#F26722]" /> {data.serial_number}</h1>
        <p className="text-zinc-400 text-sm">{t("Machine history")}</p>

        <span className="text-[11px] font-bold tracking-wider text-[#F26722] uppercase mt-6 mb-2 block">{t("Service history")}</span>
        {(data.reports || []).length === 0 && <p className="text-zinc-500 text-sm">{t("No reports for this machine.")}</p>}
        <div className="space-y-2">
          {(data.reports || []).map((r) => (
            <button key={r.id} data-testid={`machine-report-${r.id}`} onClick={() => nav(`/reports/${r.id}`)}
              className="w-full text-left bg-[#18181B] border border-zinc-800 hover:border-zinc-700 rounded-xl p-3.5 flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-bold text-white truncate flex items-center gap-2"><FileText size={14} className="text-[#F26722] shrink-0" /> {r.customer_name}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{r.report_date || (r.created_at || "").slice(0, 10)} · {r.mechanic_name || "—"}</p>
              </div>
              <StatusChip status={r.status} />
            </button>
          ))}
        </div>

        <span className="text-[11px] font-bold tracking-wider text-[#F26722] uppercase mt-7 mb-2 block">{t("Parts used")}</span>
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl divide-y divide-zinc-800/60">
          {(data.parts || []).length === 0 && <p className="text-zinc-500 text-sm p-4">{t("No parts listed.")}</p>}
          {(data.parts || []).map((p, i) => (
            <div key={i} className="flex items-center justify-between p-3.5" data-testid={`machine-part-${p.part_number}`}>
              <div className="min-w-0"><p className="font-bold text-white truncate">{p.part_name || p.part_number}</p><p className="text-xs text-[#F97316] font-mono">{p.part_number}</p></div>
              <span className="text-sm text-zinc-300 shrink-0 flex items-center gap-1"><Package size={14} className="text-[#F26722]" /> ×{p.total_qty}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
